import { config } from '@config';
import { BehaviorSubject, of, zip } from 'rxjs';
import { concatMap, tap, map, take, catchError } from 'rxjs/operators';
import { RxUtils, TypeUtils } from '@utils';
import { httpService } from '@services/http';
import { logger } from '@services/logger';
import { carListBandService } from '@services/car-list-band';
import { Quotation } from '@services/quotation';
import { Simulation } from './simulation.class';
import { SimulationTask } from './simulation-task.class';
import { SimulationTasksGroupedByCarListBand } from './simulation-tasks-grouped-by-car-list-band.class';
import { SimulationVehicle } from './simulation-vehicle.class';
import { SimulationInfrastructure } from './simulation-infrastructure.class';
import { SimulationSavings } from './simulation-savings.class';
import { SimulationKPI } from './simulation-kpi.class';

export class SimulationService {
	private cache$: BehaviorSubject<Maybe<Simulation>>;
	private tasksCache$: BehaviorSubject<Maybe<SimulationTask[]>>;

	private getUrl() {
		return `${config.apiUrl}/simulation`;
	}

	getCachedSimulation() {
		this.cache$ = this.cache$ || new BehaviorSubject<Maybe<Simulation>>(undefined);
		if (!this.cache$.getValue()) {
			return this.getLatest().pipe(
				catchError(() => of(undefined)),
				concatMap((entities) => (this.cache$.next(entities), this.cache$))
			);
		}
		return this.cache$;
	}

	getCachedTasks() {
		this.tasksCache$ = this.tasksCache$ || new BehaviorSubject<Maybe<SimulationTask[]>>(undefined);
		if (!this.tasksCache$.getValue()) {
			return this.getLatestSimulationTasks().pipe(
				catchError(() => of(undefined)),
				concatMap((entities) => (this.tasksCache$.next(entities), this.tasksCache$))
			);
		}
		return this.tasksCache$;
	}

	get(id: string) {
		return httpService.get<Simulation>(this.getUrl(), `${id}`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.get.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(Simulation, result))
		);
	}

	getInfrastructure(id: string) {
		return httpService.get<SimulationInfrastructure>(this.getUrl(), `${id}/infrastructure`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getInfrastructure.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(SimulationInfrastructure, result))
		);
	}

	getSavings(id: string) {
		return httpService.get<SimulationSavings>(this.getUrl(), `${id}/savings`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getSavings.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(SimulationSavings, result))
		);
	}

	getQuotation(id: string) {
		//TODO: domsca: rename it to assessment
		return httpService.get<Quotation>(this.getUrl(), `${id}/quotation`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getQuotation.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(Quotation, result))
		);
	}

	getKPIs(siteId?: string) {
		return httpService.get<SimulationKPI>(this.getUrl(), `/latest/kpi`, { params: siteId ? { siteId } : {} }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getKPIs.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(SimulationKPI, result))
		);
	}

	getAll() {
		return httpService.get<Simulation[]>(this.getUrl()).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getAll.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(Simulation, result))
		);
	}

	getLatest(disableLoader = false) {
		return httpService.get<Simulation[]>(this.getUrl(), 'latest/one', { disableLoader }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getLatest.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(Simulation, result)?.[0]),
			map((result) => (this.cache$?.next(result), result))
		);
	}

	getTasks(simulation: Simulation) {
		const getTasks$ = httpService.get<SimulationTask[]>(this.getUrl(), `${simulation.id}/tasks`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getTasks.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => {
				simulation?.savings?.yearlySavings?.forEach(({ year, vehiclesElectrified }) => {
					vehiclesElectrified.forEach(({ id }) => {
						const found = result.find(({ id: simId }) => simId === id);
						found && (found.electrificationYear = year);
					});
				});
				return result;
			}),
			map((result) => TypeUtils.convert(SimulationTask, result))
		);

		return zip(getTasks$, carListBandService.getAll()).pipe(
			map(([tasks, carListBands]) => {
				tasks.forEach((task) => {
					if (task?.fleetVehicle) {
						const carListBandId = task.fleetVehicle.segmentId;
						const carListBand = carListBandId ? carListBands.find((s) => s.id === carListBandId) : undefined;
						task.fleetVehicle.segment = carListBand;
					}
				});
				return tasks;
			}),
			take(1),
			map((result) => (this.tasksCache$?.next(result), result))
		);
	}

	getLatestSimulationTasks() {
		return this.getLatest().pipe(concatMap((simulation) => this.getTasks(simulation)));
	}

	getLatestSimulationTasksGroupByCarListBand() {
		return this.getLatestSimulationTasks().pipe(
			map((tasks) => SimulationTasksGroupedByCarListBand.fromTasks(tasks)),
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getLatestSimulationTasksGroupByCarListBand.name}]`, result))
		);
	}

	create() {
		return httpService.post<Simulation>(this.getUrl(), '', { errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.create.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(Simulation, result))
		);
	}

	searchVehicles({ simulationId, id }: SimulationTask, text: string) {
		return httpService.get<SimulationVehicle[]>(this.getUrl(), `${simulationId}/tasks/${id}/search-vehicles/${text}`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.searchVehicles.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((entities) => TypeUtils.convert(SimulationVehicle, entities))
		);
	}
}

export const simulationService = new SimulationService();
